import React, { useState, useLayoutEffect, useRef } from "react";
import images from "../../../images";
import Status from "../Status/Status";
import Timer from "../../../pages/Quest/DetailMultipleChoice/Timer";
import { cn, formatDateToFakeMicroUTC } from "../../../Utils/helper";
import { usePostSaveQuestMutation } from "../../../services/apiSlice";

// Object mapping questType to title, description, and images (normal and disabled)
const questTypeMap = {
  INTRODUCE: {
    title: "Introductory Quest",
    description: "Getting to know you.",
    image: images.Introduce,
    disabledImage: images.IntroduceDisable,
  },
  USER_JOURNEY: {
    title: "Experience Trail",
    description: "Mapping your experience.",
    image: images.ExperienceTRail,
    disabledImage: images.ExperienceTrailDisable,
  },
  PULSE_CHECK: {
    title: "Vibe Check",
    description: "Just checking in.",
    image: images.VibeCheck,
    disabledImage: images.VibeCheckDisable,
  },
  SNAP: {
    title: "Snap Quest",
    description: "Snap your receipt.",
    image: images.Snap,
    disabledImage: images.SnapDisable,
  },
};

const CardQuestPerTypeForHome = ({
  slotAvailable,
  consumerStatus,
  questType,
  rallyCoin,
  expireTime,
  onClick,
  isIntroCompleted = false,
  participantId,
  onQuestSaved,
  questId,
  participants,
  isEnable,
  statusBiz = "NOT_STARTED",
}) => {
  const now = new Date();
  const nowUTCISOString = formatDateToFakeMicroUTC(now);
  const expiredDate = expireTime ? new Date(expireTime.slice(0, 23)) : null;
  const nowDate = new Date(nowUTCISOString.slice(0, 23));
  const diffMs = expiredDate ? expiredDate.getTime() - nowDate.getTime() : 0;
  const diffSeconds = Math.round(diffMs / 1000);

  const initialRemainingTime = diffSeconds > 0 ? diffSeconds : 0;

  const [remainingTime, setRemainingTime] = useState(initialRemainingTime);

  const [postSaveQuest] = usePostSaveQuestMutation();
  const hasProcessedExpiredRef = useRef(false);
  const isSelfCompletedSNAP =
    Array.isArray(participants) &&
    participants.every((item) => item.status === "COMPLETED");
  const isSelfCompleted = consumerStatus === "COMPLETED" && isSelfCompletedSNAP;

  const isDisabled =
    isSelfCompleted ||
    (questType !== "INTRODUCE" &&
      !isIntroCompleted &&
      statusBiz !== "IN_PROGRESS");

  useLayoutEffect(() => {
    if (!initialRemainingTime || initialRemainingTime <= 0) {
      if (
        consumerStatus === "PAUSED_QUEST" &&
        participantId &&
        !hasProcessedExpiredRef.current
      ) {
        hasProcessedExpiredRef.current = true;

        const handleQuestEnd = async () => {
          try {
            await postSaveQuest({
              participant_id: participantId,
            }).unwrap();

            if (onQuestSaved) {
              onQuestSaved(questId, "saved");
            }
          } catch (error) {
            console.error(`❌ Error saving quest ${questId}:`, error);
            hasProcessedExpiredRef.current = false;
          }
        };

        handleQuestEnd();
      }
      setRemainingTime(0);
      return;
    }

    hasProcessedExpiredRef.current = false;

    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        const newTime = prevTime - 1;

        if (
          newTime <= 0 &&
          consumerStatus === "PAUSED_QUEST" &&
          participantId &&
          !hasProcessedExpiredRef.current
        ) {
          hasProcessedExpiredRef.current = true;

          const handleTimerEnd = async () => {
            try {
              await postSaveQuest({
                participant_id: participantId,
              }).unwrap();

              if (onQuestSaved) {
                onQuestSaved(questId, "saved");
              }
            } catch (error) {
              console.error(`❌ Error auto-saving quest ${questId}:`, error);
              hasProcessedExpiredRef.current = false;
            }
          };

          handleTimerEnd();
          clearInterval(timer);
          return 0;
        }

        return Math.max(newTime, 0);
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [
    initialRemainingTime,
    consumerStatus,
    participantId,
    postSaveQuest,
    questId,
    onQuestSaved,
    expireTime,
  ]);

  const { title, description, image, disabledImage } =
    questTypeMap[questType] || questTypeMap["PULSE_CHECK"];

  const displayImage = isDisabled ? disabledImage : image;
  return (
    <div>
      <div
        onClick={onClick}
        className={cn(
          "flex justify-between border-[#ECEEFF] border-solid border-[1px] rounded-md bg-[#F6F7FF] min-h-[170px]",
          consumerStatus === "PAUSED_QUEST" && "rounded-b-none border-b-0",
          isDisabled && "bg-[rgba(233,230,228,0.5)]"
        )}
      >
        <div className="flex flex-row justify-between gap-2 w-full">
          <div className="flex flex-col justify-between items-start flex-1 self-stretch p-[18px]">
            <div>
              <h4
                className={cn(
                  "text-[#253FF6] font-[Figtree] text-[24px] font-bold not-italic leading-none",
                  isDisabled && "text-[#D0D0D0]"
                )}
              >
                {title}
              </h4>
              <p
                className={cn(
                  "text-[#253FF6] font-[Figtree] text-[14px] font-medium not-italic leading-[21px]",
                  isDisabled && "text-[#D0D0D0]"
                )}
              >
                {description}
              </p>
            </div>
            <div className="flex flex-row gap-2 items-center bg-[#ECEEFF] px-2 py-1 rounded-md">
              <img
                src={images.RallyCoinSmallBlueIcon}
                className="w-4 h-4"
                alt=""
              />
              <h6 className="text-[#253FF6] font-[Figtree] text-[18px] font-bold not-italic leading-[28px]">
                {rallyCoin || 50}
              </h6>
            </div>
          </div>
          <div className="flex flex-col justify-between items-center pt-[18px] min-w-[100px]">
            <Status
              status={
                consumerStatus === "COMPLETED" && isSelfCompletedSNAP === false
                  ? "saved"
                  : isDisabled && consumerStatus !== "COMPLETED"
                  ? "ineligible"
                  : consumerStatus === "NOT_STARTED"
                  ? "eligible"
                  : consumerStatus === "SAVE_QUEST"
                  ? "saved"
                  : consumerStatus === "PAUSED_QUEST"
                  ? "paused"
                  : consumerStatus === "REVIEWING"
                  ? "review"
                  : consumerStatus === "COMPLETED"
                  ? "completed"
                  : "eligible"
              }
              isDisabled={isDisabled}
            />
            <img src={displayImage} alt="" />
          </div>
        </div>
      </div>

      {consumerStatus === "PAUSED_QUEST" ? (
        <Timer className={"mt-0"} time={remainingTime} isStatic={true} />
      ) : (
        <></>
      )}
    </div>
  );
};

export default CardQuestPerTypeForHome;
